import {
  Button,
  Card,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Table,
  TableColumnType,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  IPurchaseOrderDetail,
  IPurchaseOrderItem,
  IUpdatePurchaseOrder,
} from "../../models/Purchase";
import { useUpdatePurchaseOrder } from "../../query/PurchaseQuery";
import { notify } from "../../utils/toast";

interface IModalEditPurchaseOrderProps {
  open: boolean;
  onClose: () => void;
  purchaseOrder: IPurchaseOrderDetail;
}

interface IEditOrderItem extends IPurchaseOrderItem {
  quantityBuying: number;
  unitPriceExwVnd: number;
  totalExwVnd: number;
  totalImportTax: number;
}

interface IFormData {
  vendorName: string;
  orderDate: Dayjs | null;
  expectedDeliveryDate: Dayjs | null;
  shippingCharge: number;
  tax: number;
  note: string;
  exchangeRate: number;
}

const ModalEditPurchaseOrder = ({
  open,
  onClose,
  purchaseOrder,
}: IModalEditPurchaseOrderProps) => {
  const [orderItems, setOrderItems] = useState<IEditOrderItem[]>([]);
  const [formData, setFormData] = useState<IFormData>({
    vendorName: "",
    orderDate: null,
    expectedDeliveryDate: null,
    shippingCharge: 0,
    tax: 0,
    note: "",
    exchangeRate: 26000,
  });

  const updatePurchaseOrderMutation = useUpdatePurchaseOrder();

  useEffect(() => {
    if (purchaseOrder && open) {
      setFormData({
        vendorName: purchaseOrder.vendorName,
        orderDate: dayjs(purchaseOrder.orderDate),
        expectedDeliveryDate: dayjs(purchaseOrder.expectedDeliveryDate),
        shippingCharge: purchaseOrder.shippingCharge,
        tax: purchaseOrder.importTaxPercent,
        note: purchaseOrder.note || "",
        exchangeRate: purchaseOrder.exchangeRate,
      });

      setOrderItems(
        purchaseOrder.items.map((item) => ({
          ...item,
          quantityBuying: item.quantityBuying,
          unitPriceExwVnd: item.unitPriceExwVnd,
          totalExwVnd: item.totalExwVnd,
          totalImportTax: item.totalImportTax,
        })),
      );
    }
  }, [purchaseOrder, open]);

  const updateOrderItem = (
    id: number,
    field: keyof IEditOrderItem,
    value: number,
  ) => {
    setOrderItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "quantityBuying" || field === "unitPriceExwVnd") {
            updated.totalExwVnd =
              updated.quantityBuying * updated.unitPriceExwVnd;
            updated.totalImportTax = (updated.totalExwVnd * formData.tax) / 100;
          }
          return updated;
        }
        return item;
      }),
    );
  };

  const handleTaxPercentChange = (value: number | null) => {
    const newTaxPercent = value || 0;
    setOrderItems((prev) =>
      prev.map((item) => ({
        ...item,
        totalImportTax: (item.totalExwVnd * newTaxPercent) / 100,
      })),
    );
  };

  const handleSubmit = async () => {
    try {
      if (
        !formData.vendorName ||
        !formData.orderDate ||
        !formData.expectedDeliveryDate
      ) {
        notify.error("Vui lòng điền đầy đủ thông tin bắt buộc");
        return;
      }

      if (orderItems.length === 0) {
        notify.error("Đơn hàng phải có ít nhất một vật tư");
        return;
      }

      const payload: IUpdatePurchaseOrder = {
        vendorName: formData.vendorName,
        orderDate: formData.orderDate.toDate(),
        expectedDeliveryDate: formData.expectedDeliveryDate.toDate(),
        shippingCharge: formData.shippingCharge || 0,
        importTaxPercent: formData.tax || 0,
        exchangeRate: formData.exchangeRate || 0,
        note: formData.note || "",
        items: orderItems.map((item) => ({
          purchaseOrderItemId: item.id,
          quantityBuying: item.quantityBuying,
          unitPriceExwVnd: item.unitPriceExwVnd,
          totalExwVnd: item.totalExwVnd,
        })),
      };

      await updatePurchaseOrderMutation.mutateAsync({
        id: purchaseOrder.id,
        data: payload,
      });
      notify.success("Cập nhật đơn đặt hàng thành công");
      onClose();
    } catch (error) {
      notify.error("Lỗi khi cập nhật đơn đặt hàng");
    }
  };

  const orderColumns: TableColumnType<IEditOrderItem>[] = [
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 150,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      width: 200,
    },
    {
      title: "SL mua",
      key: "quantityBuying",
      width: 120,
      render: (_: any, record: IEditOrderItem) => (
        <InputNumber
          value={record.quantityBuying}
          onChange={(value) =>
            updateOrderItem(record.id, "quantityBuying", value || 0)
          }
          min={1}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Đơn giá EXW",
      key: "unitPriceExwVnd",
      width: 150,
      render: (_: any, record: IEditOrderItem) => (
        <InputNumber
          value={record.unitPriceExwVnd}
          onChange={(value) =>
            updateOrderItem(record.id, "unitPriceExwVnd", value || 0)
          }
          min={0}
          style={{ width: "100%" }}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
        />
      ),
    },
    {
      title: "Tổng EXW",
      dataIndex: "totalExwVnd",
      key: "totalExw",
      width: 150,
      render: (value: number) => `${Number(value).toLocaleString()} VNĐ`,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Chỉnh sửa đơn đặt hàng"
      width={1800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Hủy
        </Button>,
        <Popconfirm
          title="Bạn có chắc chắn muốn cập nhật đơn đặt hàng?"
          onConfirm={handleSubmit}
          okText="Xác nhận"
          cancelText="Hủy"
        >
          <Button
            key="submit"
            type="primary"
            loading={updatePurchaseOrderMutation.isPending}
          >
            Cập nhật
          </Button>
        </Popconfirm>,
      ]}
    >
      <div>
        <Card title="Thông tin đơn hàng" size="small">
          <div className="mt-4 p-4 rounded border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tên nhà cung cấp
                </label>
                <Input
                  value={formData.vendorName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      vendorName: e.target.value,
                    })
                  }
                  placeholder="Nhập tên nhà cung cấp"
                  size="large"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Ngày đặt hàng
                </label>
                <DatePicker
                  value={formData.orderDate}
                  onChange={(date) =>
                    setFormData({ ...formData, orderDate: date })
                  }
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày đặt hàng"
                  size="large"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Ngày giao hàng
                </label>
                <DatePicker
                  value={formData.expectedDeliveryDate}
                  onChange={(date) =>
                    setFormData({
                      ...formData,
                      expectedDeliveryDate: date,
                    })
                  }
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày giao hàng"
                  size="large"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Phí ship (VNĐ)
                </label>
                <InputNumber
                  value={formData.shippingCharge}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      shippingCharge: value || 0,
                    })
                  }
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/,/g, ""))}
                  size="large"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Phí tax (%)
                </label>
                <InputNumber
                  value={formData.tax}
                  onChange={(value) => {
                    const newTax = value || 0;
                    setFormData({ ...formData, tax: newTax });
                    handleTaxPercentChange(newTax);
                  }}
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                  max={100}
                  size="large"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tỉ giá</label>
                <InputNumber
                  value={formData.exchangeRate}
                  onChange={(e) => {
                    const newExchangeRate = e || 0;
                    setFormData({
                      ...formData,
                      exchangeRate: newExchangeRate,
                    });
                  }}
                  style={{ width: "100%" }}
                  placeholder="Tỉ giá"
                  size="large"
                />
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium mb-2">
                  Ghi chú
                </label>
                <Input.TextArea
                  value={formData.note}
                  onChange={(e) =>
                    setFormData({ ...formData, note: e.target.value })
                  }
                  placeholder="Ghi chú thêm"
                  rows={3}
                />
              </div>
            </div>
          </div>
        </Card>

        <h4 className="mt-4 mb-2">Danh sách vật tư:</h4>
        <Table
          dataSource={orderItems}
          columns={orderColumns}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 1000, y: 300 }}
        />

        <div className="mt-4 p-3 rounded">
          <div className="flex justify-between">
            <span className="font-semibold">Tổng EXW:</span>
            <span className="text-lg font-bold text-blue-600">
              {orderItems
                .reduce((sum, item) => sum + Number(item.totalExwVnd), 0)
                .toLocaleString()}{" "}
              VNĐ
            </span>
          </div>
          <div className="flex justify-between mt-2">
            <span className="font-semibold">Tổng thuế nhập khẩu:</span>
            <span className="text-lg font-bold text-orange-600">
              {orderItems
                .reduce((sum, item) => sum + Number(item.totalImportTax), 0)
                .toLocaleString()}{" "}
              VNĐ
            </span>
          </div>
          <div className="flex justify-between mt-2">
            <span className="font-semibold">Phí vận chuyển:</span>
            <span className="text-lg font-bold text-yellow-600">
              {formData.shippingCharge.toLocaleString()} VNĐ
            </span>
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t">
            <span className="font-semibold">Tổng cộng (VND):</span>
            <span className="text-xl font-bold text-green-600">
              {(
                orderItems.reduce(
                  (sum, item) =>
                    sum +
                    Number(item.totalExwVnd) +
                    Number(item.totalImportTax),
                  0,
                ) + Number(formData.shippingCharge)
              ).toLocaleString()}{" "}
              VNĐ
            </span>
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t">
            <span className="font-semibold">Tổng cộng ($):</span>
            <span className="text-xl font-bold text-green-600">
              {(
                (orderItems.reduce(
                  (sum, item) =>
                    sum +
                    Number(item.totalExwVnd) +
                    Number(item.totalImportTax),
                  0,
                ) +
                  Number(formData.shippingCharge)) /
                Number(formData.exchangeRate)
              ).toFixed(2)}{" "}
              $
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalEditPurchaseOrder;
